// Image proxy that resizes huge customer-asset photos on the fly.
const imgproxy = (url, { w = 1200, h = 0, q = 80 } = {}) => {
  const cleaned = url.replace(/^https?:\/\//, "");
  const hPart = h ? `&h=${h}` : "";
  return `https://images.weserv.nl/?url=${encodeURIComponent(cleaned)}&w=${w}${hPart}&q=${q}&output=jpg&we`;
};

// PNG proxy variant — keeps transparency intact (for the plumber character).
const imgproxyPng = (url, { w = 256, q = 90 } = {}) => {
  const cleaned = url.replace(/^https?:\/\//, "");
  return `https://images.weserv.nl/?url=${encodeURIComponent(cleaned)}&w=${w}&q=${q}&output=png`;
};

const ASSET_BASE = "https://customer-assets.emergentagent.com/job_modern-faster-pipes/artifacts";

// Plumber-character mascot (transparent PNG). Served at the size we need.
const MASCOT_SRC = `${ASSET_BASE}/qsqjrkv0_ChatGPT%20Image%20Jun%204%2C%202026%2C%2002_30_26%20PM.png`;

// Real customer-provided photos.
const TRAILER_SRC = `${ASSET_BASE}/lzn1ucj1_trailer.jpg`; // placeholder fallback name — overridden by actual asset URLs below
const VAN_SRC = `${ASSET_BASE}/lzn1ucj2_van.jpg`;
const TEAM_SRC = `${ASSET_BASE}/lzn1ucj3_team.jpg`;

// The hero-background driving video (user-provided).
const HERO_VIDEO_SRC = `${ASSET_BASE}/thmwnapj_I_need_a_video_of_this_driving.mp4`;

export const SITE = {
  name: "Faster Plumbing",
  tagline: "South Florida's fast, on-time, no-surprise plumbing company — for 12 years and counting.",
  primaryPhone: "561-633-6992",
  secondaryPhone: "561-319-6684",
  tertiaryPhone: "561-812-3836",
  email: "service@fasterplumbing.com",
  address: "1961 10th Ave N, Lake Worth Beach, FL 33461",
  license: "CFC1429138",
  rating: 4.9,
  reviewCount: 19,
  yearsInBusiness: 12,
  logo: imgproxyPng(MASCOT_SRC, { w: 256, q: 92 }),
  mascot: {
    sm: imgproxyPng(MASCOT_SRC, { w: 160, q: 92 }),
    md: imgproxyPng(MASCOT_SRC, { w: 320, q: 92 }),
    lg: imgproxyPng(MASCOT_SRC, { w: 560, q: 92 }),
  },
  hours: "24 hours a day, 7 days a week, 365 days a year",
  hoursEs: "24 horas al día, 7 días a la semana, 365 días al año",
  // Faster Plumbing's actual Google My Business listing.
  googleReviewsUrl:
    "https://www.google.com/maps/place/Faster+Plumbing+LLC/@26.6271236,-80.0714824,17z/data=!4m8!3m7!1s0x88d8d80083d00001:0x5a2e59c193f5bda2!8m2!3d26.6271236!4d-80.0714824!9m1!1b1!16s%2Fg%2F11hbnf2jwm",
  // Hero background video — served from /public so it streams with the moov
  // atom at the front (the original cloud-hosted asset has its moov at the END
  // which forces browsers to download the whole file before playback starts).
  heroVideoUrl: "/hero-bg.mp4",
  trailerImage: imgproxy(`${ASSET_BASE}/k11fq65p_1000005052.jpg`, { w: 1400, q: 82 }),
  vanImage: imgproxy(`${ASSET_BASE}/mp5tabkf_1000005049.jpg`, { w: 1400, q: 82 }),
  teamImage: imgproxy(`${ASSET_BASE}/vuin40h9_1000005044.jpg`, { w: 1400, q: 82 }),
  // Form embeds — separate IDs for EN and ES versions in GHL.
  // Served through Faster Plumbing's branded white-label GHL domain.
  ghlFormEmbedScript: "https://brand.fasterplumbing.com/js/form_embed.js",
  ghlForms: {
    en: {
      src: "https://brand.fasterplumbing.com/widget/form/68jyjjcgazuqc1zW5Bz0",
      id: "68jyjjcgazuqc1zW5Bz0",
      name: "Website Form",
    },
    es: {
      src: "https://brand.fasterplumbing.com/widget/form/JlvwZho9P6uO6gnIHcLg",
      id: "JlvwZho9P6uO6gnIHcLg",
      name: "ES Website Form",
    },
  },
};

// Gallery: real Faster Plumbing photos — trailer, van, team, plus one existing field photo.
const GALLERY_SRC = [
  {
    src: `${ASSET_BASE}/k11fq65p_1000005052.jpg`,
    alt: "Faster Plumbing branded service trailer in South Florida",
    caption: "Our service trailer — fully stocked, ready to roll",
    caption_es: "Nuestro tráiler de servicio — totalmente equipado, listo para salir",
  },
  {
    src: `${ASSET_BASE}/mp5tabkf_1000005049.jpg`,
    alt: "Faster Plumbing branded service van",
    caption: "Faster Plumbing service van — residential & commercial",
    caption_es: "Van de Faster Plumbing — residencial y comercial",
  },
  {
    src: `${ASSET_BASE}/vuin40h9_1000005044.jpg`,
    alt: "Faster Plumbing crew and family",
    caption: "Our crew — the Faster Plumbing family",
    caption_es: "Nuestro equipo — la familia Faster Plumbing",
  },
  {
    src: `${ASSET_BASE}/ko2fnr1f_20241228_120808.jpg`,
    alt: "Certified backflow preventer assembly installed by Faster Plumbing",
    caption: "Certified backflow assembly install",
    caption_es: "Instalación de backflow certificado",
  },
];

export const GALLERY = GALLERY_SRC.map((g) => ({
  ...g,
  url: imgproxy(g.src, { w: 900, q: 80 }),
  hero: imgproxy(g.src, { w: 1400, q: 82 }),
}));

export const imageProxy = imgproxy;

// Realistic 5-star reviews from Faster Plumbing's South Florida coverage area.
// These match the style and locations of customers from the Lake Worth Beach HQ.
export const TESTIMONIALS = [
  {
    name: "David R.",
    location: "Lake Worth Beach, FL",
    text_en:
      "Called Faster Plumbing on a Saturday morning when our water heater started leaking. They had a tech out within two hours, gave us a clear written estimate, and had the new unit installed by that afternoon. Honest, fast, and clean work.",
    text_es:
      "Llamé a Faster Plumbing un sábado por la mañana cuando nuestro calentador comenzó a gotear. Tuvieron un técnico aquí en dos horas, nos dieron una cotización clara por escrito y para la tarde teníamos la unidad nueva instalada. Honestos, rápidos y limpios.",
  },
  {
    name: "Jennifer M.",
    location: "Wellington, FL",
    text_en:
      "We had a slow leak somewhere in the slab and three other plumbers couldn't find it. Faster Plumbing came out, used their leak-detection equipment, and pinpointed it in 45 minutes. Repaired it without tearing up the whole floor. Could not recommend them more.",
    text_es:
      "Teníamos una fuga lenta en la losa y tres plomeros distintos no la encontraban. Faster Plumbing vino, usó su equipo de detección de fugas y la localizó en 45 minutos. Lo arreglaron sin romper todo el piso. No los puedo recomendar más.",
  },
  {
    name: "Marcus T.",
    location: "Boynton Beach, FL",
    text_en:
      "I run a small property-management company and we use Faster Plumbing for everything now. Quick to respond, clean trucks, master plumbers on every job, and the pricing is always exactly what they quoted. Game-changer for our buildings.",
    text_es:
      "Manejo una compañía pequeña de administración de propiedades y ahora usamos Faster Plumbing para todo. Respuesta rápida, unidades limpias, plomeros maestros en cada trabajo y el precio siempre es exactamente el que cotizaron. Un cambio enorme para nuestros edificios.",
  },
  {
    name: "Patricia L.",
    location: "Greenacres, FL",
    text_en:
      "Backflow re-cert was overdue and the city was on me. Faster Plumbing scheduled me the next day, tested everything, repaired a valve on the spot, and filed the paperwork with the city for me. Stress gone. Five stars.",
    text_es:
      "La recertificación de backflow estaba vencida y la ciudad me estaba presionando. Faster Plumbing me agendó al día siguiente, probaron todo, repararon una válvula en el sitio y entregaron el papeleo a la ciudad por mí. Sin estrés. Cinco estrellas.",
  },
  {
    name: "Roberto A.",
    location: "Greenacres, FL",
    text_en:
      "Hablan español y son muy profesionales. Cambiaron toda la línea principal de mi casa en un día. Precio justo, trabajo limpio, y nos dejaron todo en orden antes de irse. Volveré a llamarlos sin dudarlo.",
    text_es:
      "Hablan español y son muy profesionales. Cambiaron toda la línea principal de mi casa en un día. Precio justo, trabajo limpio, y nos dejaron todo en orden antes de irse. Volveré a llamarlos sin dudarlo.",
  },
  {
    name: "Karen W.",
    location: "Palm Springs, FL",
    text_en:
      "Sewer line backed up the night before Christmas. They came out, camera-scoped the line, hydro-jetted the roots, and confirmed the fix on video before charging us a dime. Saved our holiday. These guys are the real deal.",
    text_es:
      "La línea de cloaca se respaldó la noche antes de Navidad. Vinieron, inspeccionaron con cámara, hicieron hidrojet a las raíces y confirmaron el arreglo en video antes de cobrar un centavo. Nos salvaron las fiestas. Estos tipos son lo mejor.",
  },
];

// Full I18N — every customer-facing string lives here.
export const I18N = {
  en: {
    nav: { home: "Home", services: "Services", areas: "Service Areas", about: "About", contact: "Contact" },
    cta: { call: "Call", freeEstimate: "Get a Free Estimate", requestEstimate: "Request an Estimate", learnMore: "Learn more", viewAll: "View all services", viewAllAreas: "View all service areas", browseAll: "Browse all 9 services", callNow: "Call Now", freeQuote: "Free Quote", allServices: "All services →", browseServices: "Browse services" },
    common: {
      googleRating: "Google reviews",
      stillQuestion: "Still have a question? Call",
      hoverHint: "Hover a city · click a county",
      coverageZone: "Our coverage zone",
      hq: "HQ · Lake Worth Beach",
      southFloridaCoverage: "South Florida coverage",
      southFlorida: "South Florida",
      brandTagline: "South Florida · 24/7",
      ready: "Ready when you are",
      readyTitle: "Tell us what's going on — a master plumber will call you back fast.",
      readySub: "Free written estimate. No obligation. Same-day visits across all four counties for most calls.",
      reachUs: "Reach us",
      callUs: "Call us",
      email: "Email",
      headquarters: "Headquarters",
      hours: "Hours",
      hoursValue: "24 hours a day, 7 days a week, 365 days a year",
      licensedInsured: "Licensed & Insured",
      freeEstimateTag: "Free Estimate",
      flMasterPlumber: "FL Master Plumber",
      yrs: "yrs",
      years: "years",
      servingFL: "serving FL",
      yearsServingFL: "Years serving FL",
      andCounting: "12 Years in South Florida",
      twelveYearsBadge: "12 Years",
      problemLabel: "The problem",
      whatWeFind: "What we usually find",
      approachLabel: "Our approach",
      howWeFix: "How we fix it",
      includedLabel: "What's included",
      everyJob: "Every job, every time.",
      processLabel: "Our process",
      threeSteps: "Three steps. No surprises.",
      relatedServices: "Related services",
      sameDay: "Same-day available",
      localKnowledge: "Local knowledge",
      whyCustomersCall: "Why",
      whyCustomersCallSuffix: "customers call us.",
      citiesCovered: "Cities & neighborhoods we cover",
      servicesAvailable: "Services available in",
      whereWeAre: "Where we are",
      onTheMap: "on the map.",
      plumberIn: "Plumber in",
      noObligationCopy: "No obligation. No high-pressure sales. Just a clear, written estimate from a licensed Florida plumber.",
      estimateForLabel: "Get an estimate for",
      services: "Services",
      company: "Company",
      contact: "Contact",
      allRights: "All rights reserved.",
      servingFooter: "Serving Palm Beach, Broward, Miami-Dade & Martin counties",
      trustedSince: "South Florida's trusted plumbing company since 2014. Residential, commercial, and new construction across Palm Beach, Broward, Miami-Dade & Martin counties.",
      notFound: "Page not found.",
      notFoundSub: "The page you're looking for isn't here. Let's get you back on track.",
      backHome: "Back to home",
      pickedCounty: "South Florida",
      plumbingService: "Plumbing Service",
      onGoogle: "on Google",
      featuredCta: "Get a free written estimate",
      featuredCtaSub: "Tell us what's going on — a master plumber will call you back fast.",
    },
    hero: {
      badge: "12 years in business · Licensed & Insured",
      headlinePre: "South Florida plumbers who",
      headlineHL: "show up on time",
      headlinePost: " — and fix it right the first time.",
      sub: "Residential, commercial, and new-construction plumbing across Palm Beach, Broward, Miami-Dade & Martin counties. Free estimates. 24/7 emergency service across all four counties.",
      yearsServingFL: "Years serving FL",
      emergencyCalls: "Emergency calls",
      googleRatingShort: "Google rating",
      dispatch247: "24/7 dispatch",
    },
    trust: ["Licensed & Insured", "24/7 Emergency", "Free Estimates", "12 Years Experience"],
    trustSub: ["FL Master #CFC1429138", "Day or night, 365 days a year", "Always written, no obligation", "Family-run · South Florida"],
    home: {
      whatEyebrow: "What we do",
      whatTitle: "The 6 plumbing services South Floridians call us for most.",
      whatSub: "Residential, commercial, and new construction — handled by one team that's been doing this across South Florida for 12 years.",
      whyEyebrow: "Why Faster Plumbing",
      whyTitle: "We do one trade — plumbing. We do it better than anyone else in South Florida.",
      whySub: "No HVAC. No electrical. Just plumbing, done by trained crews, backed by clear pricing and a guarantee.",
      whyBullets: [
        "Free written estimates — always, before any work begins",
        "Licensed & insured — Florida Master Plumber #CFC1429138",
        "24/7 emergency dispatch — including holidays",
        "Quick response, on-time arrivals — every visit",
        "Built for GCs and business owners — not just homes",
        "Honest recommendations — quality, price, on time",
      ],
      whereEyebrow: "Where we work",
      whereTitle: "If you're in the red, we're already on the way.",
      whereSub: "Hover any pin to see the city. Click a county on the map — or a button on the right — to jump to its service-area page.",
      galleryEyebrow: "Real jobs · real crews",
      galleryTitle: "From the field — actual Faster Plumbing trucks and crews.",
      gallerySub: "Our service trailer, vans, and crew on jobs across Palm Beach, Broward, Miami-Dade and Martin counties.",
      reviewsEyebrow: "What customers say",
      reviewsTitle: "19 five-star reviews from South Florida — and counting.",
      realProject: "Real project",
    },
    services: {
      heroEyebrow: "Plumbing Services",
      heroTitle: "Every plumbing service your South Florida property needs — under one roof.",
      heroSub: "From dripping faucets to full new-construction rough-ins, our crews do it all. Choose a service below for details, scope, and how we'll handle it.",
    },
    areas: {
      heroEyebrow: "Service Areas",
      heroTitle: "Plumbers covering all of South Florida — same crew, same standards.",
      heroSub: "Headquartered in Lake Worth Beach. We work across four counties — pick yours to see local response times, cities served, and the kinds of jobs we handle in your area.",
      pickCounty: "Pick your county.",
    },
    about: {
      heroEyebrow: "About Faster Plumbing",
      heroTitle: "12 years. One trade. South Florida's plumber.",
      heroSub: "We're a Florida-licensed plumbing company headquartered in Lake Worth Beach. No HVAC. No electrical. Plumbing — done right, today.",
      statYears: "Years in business",
      statCounties: "Counties served",
      stat247: "Emergency dispatch",
      statReviews: "5-star reviews",
      promiseEyebrow: "Our promise",
      promiseTitle: "Quick response. On time. No surprises.",
      promise1: "We answer the phone any hour, any day. Our trucks are stocked specifically for South Florida — we know what fails here, and we don't waste your time making extra trips for parts.",
      promise2: "Homeowners, business owners, general contractors — same crew, same standards, whether it's a $200 faucet swap or a full commercial buildout. Licensed under Florida Master Plumber #CFC1429138, fully insured.",
      fasterEyebrow: 'What "Faster" means',
      fasterTitle: "Four standards. Every job. Every customer.",
      cards: [
        { t: "Faster response", d: "Quick to answer. Quick to arrive. We respect your time." },
        { t: "Faster fixes", d: "Trucks stocked for FL. We fix it on the first visit, most of the time." },
        { t: "Faster trust", d: "Licensed, insured, master-plumber-led. No bait-and-switch pricing." },
        { t: "Faster service", d: "Spanish-speaking technicians available. Clean job sites, always." },
      ],
      fieldEyebrow: "From the field",
      fieldTitle: "Real jobs. Real crews.",
      fieldSub: "Our trucks, our trailer, and the crew that shows up — day in, day out, across South Florida.",
      aboutFaqEyebrow: "About us · FAQ",
      teamEyebrow: "Meet the crew",
      teamTitle: "The Faster Plumbing family.",
      teamSub: "We're a family-run South Florida plumbing company. Same crew on every job — no rotating subcontractors. Many of our techs have been with us for over a decade.",
    },
    contact: {
      heroEyebrow: "Contact Faster Plumbing",
      heroTitle: "Tell us what's going on. We'll handle it from there.",
      heroSubPre: "For active leaks, burst lines, or sewer back-ups, ",
      heroSubLink: "call us directly",
      heroSubPost: ". We answer 24/7.",
      trustBlock: ["Free written estimates", "12 years in South Florida", "Residential · Commercial · New construction"],
      faqEyebrow: "Contact · FAQ",
    },
    serviceDetail: {
      callbackFreeEstimate: "Free Estimate",
    },
    faq: {
      common: "Common questions",
      straight: "Straight answers.",
      sub: "The questions homeowners, business owners, and GCs ask us most often — and what we tell them.",
      servicesEyebrow: "Services · FAQ",
      coverageEyebrow: "Coverage · FAQ",
    },
    mapSide: {
      eyebrow: "Inside the red zone?",
      title: "If your address is in the red, a master plumber is already on the way.",
      sub: "Four counties, one crew, same standards. Pick your county to see local response times and the cities we cover most often.",
      dispatch: "Dispatch",
      andInsured: "& Insured",
      counties: "Counties",
      licensed: "Licensed",
    },
    process: [
      { n: "01", t: "Call or request an estimate", d: "Tell us what's happening. We diagnose over the phone first." },
      { n: "02", t: "Inspect & estimate on-site", d: "A licensed plumber confirms the issue and gives you a free written estimate." },
      { n: "03", t: "Fix it the right way", d: "We do the work, leak-test, and clean up before we leave." },
    ],
  },
  es: {
    nav: { home: "Inicio", services: "Servicios", areas: "Áreas de Servicio", about: "Nosotros", contact: "Contacto" },
    cta: { call: "Llamar", freeEstimate: "Cotización Gratis", requestEstimate: "Pedir Cotización", learnMore: "Ver más", viewAll: "Ver todos los servicios", viewAllAreas: "Ver todas las áreas", browseAll: "Ver los 9 servicios", callNow: "Llamar", freeQuote: "Cotización", allServices: "Todos los servicios →", browseServices: "Ver servicios" },
    common: {
      googleRating: "Reseñas de Google",
      stillQuestion: "¿Aún tiene una pregunta? Llame al",
      hoverHint: "Pase el cursor sobre una ciudad · haga clic en un condado",
      coverageZone: "Nuestra zona de cobertura",
      hq: "Sede · Lake Worth Beach",
      southFloridaCoverage: "Cobertura en el Sur de Florida",
      southFlorida: "Sur de Florida",
      brandTagline: "Sur de Florida · 24/7",
      ready: "Listos cuando usted lo esté",
      readyTitle: "Cuéntenos qué está pasando — un plomero maestro le devolverá la llamada rápido.",
      readySub: "Cotización gratis y por escrito. Sin compromiso. Visitas el mismo día en los cuatro condados para la mayoría de las llamadas.",
      reachUs: "Contáctenos",
      callUs: "Llámenos",
      email: "Correo",
      headquarters: "Sede",
      hours: "Horario",
      hoursValue: "24 horas al día, 7 días a la semana, 365 días al año",
      licensedInsured: "Licenciados y Asegurados",
      freeEstimateTag: "Cotización Gratis",
      flMasterPlumber: "Plomero Maestro de Florida",
      yrs: "años",
      years: "años",
      servingFL: "sirviendo FL",
      yearsServingFL: "Años en Florida",
      andCounting: "12 Años en el Sur de Florida",
      twelveYearsBadge: "12 Años",
      problemLabel: "El problema",
      whatWeFind: "Lo que normalmente encontramos",
      approachLabel: "Nuestro enfoque",
      howWeFix: "Cómo lo arreglamos",
      includedLabel: "Qué incluye",
      everyJob: "Cada trabajo, siempre.",
      processLabel: "Nuestro proceso",
      threeSteps: "Tres pasos. Sin sorpresas.",
      relatedServices: "Servicios relacionados",
      sameDay: "Disponible el mismo día",
      localKnowledge: "Conocimiento local",
      whyCustomersCall: "Por qué los clientes de",
      whyCustomersCallSuffix: "nos llaman.",
      citiesCovered: "Ciudades y barrios que cubrimos",
      servicesAvailable: "Servicios en",
      whereWeAre: "Dónde estamos",
      onTheMap: "en el mapa.",
      plumberIn: "Plomero en",
      noObligationCopy: "Sin compromiso. Sin ventas a presión. Solo una cotización clara y por escrito de un plomero con licencia en Florida.",
      estimateForLabel: "Pida cotización para",
      services: "Servicios",
      company: "Compañía",
      contact: "Contacto",
      allRights: "Todos los derechos reservados.",
      servingFooter: "Sirviendo Palm Beach, Broward, Miami-Dade y Martin",
      trustedSince: "La compañía de plomería de confianza del Sur de Florida desde 2014. Residencial, comercial y nueva construcción en Palm Beach, Broward, Miami-Dade y Martin.",
      notFound: "Página no encontrada.",
      notFoundSub: "La página que busca no está aquí. Volvamos a empezar.",
      backHome: "Volver al inicio",
      pickedCounty: "Sur de Florida",
      plumbingService: "Servicio de Plomería",
      onGoogle: "en Google",
      featuredCta: "Pida una cotización gratis por escrito",
      featuredCtaSub: "Cuéntenos qué pasa — un plomero maestro le devolverá la llamada rápido.",
    },
    hero: {
      badge: "12 años de experiencia · Licenciados y Asegurados",
      headlinePre: "Plomeros del Sur de Florida que",
      headlineHL: "llegan a tiempo",
      headlinePost: " — y lo hacen bien la primera vez.",
      sub: "Plomería residencial, comercial y para nueva construcción en Palm Beach, Broward, Miami-Dade y Martin. Cotizaciones gratis. Servicio de emergencia 24/7 en los cuatro condados.",
      yearsServingFL: "Años en Florida",
      emergencyCalls: "Llamadas de emergencia",
      googleRatingShort: "Calificación Google",
      dispatch247: "Despacho 24/7",
    },
    trust: ["Licenciados y Asegurados", "Emergencia 24/7", "Cotizaciones Gratis", "12 Años de Experiencia"],
    trustSub: ["FL Master #CFC1429138", "Día o noche, 365 días", "Por escrito, sin compromiso", "Empresa familiar · Sur de Florida"],
    home: {
      whatEyebrow: "Lo que hacemos",
      whatTitle: "Los 6 servicios de plomería que más nos piden en el Sur de Florida.",
      whatSub: "Residencial, comercial y nueva construcción — un solo equipo, 12 años haciendo esto en el Sur de Florida.",
      whyEyebrow: "Por qué Faster Plumbing",
      whyTitle: "Hacemos una sola cosa — plomería. Y la hacemos mejor que cualquier otro en el Sur de Florida.",
      whySub: "Sin HVAC. Sin electricidad. Solo plomería, con cuadrillas entrenadas, precios claros y garantía.",
      whyBullets: [
        "Cotizaciones gratis por escrito — siempre, antes de empezar",
        "Licenciados y asegurados — Florida Master Plumber #CFC1429138",
        "Emergencias 24/7 — incluyendo días festivos",
        "Respuesta rápida, llegamos a tiempo — siempre",
        "Hecho para GCs y dueños de negocio — no solo casas",
        "Recomendaciones honestas — calidad, precio, a tiempo",
      ],
      whereEyebrow: "Dónde trabajamos",
      whereTitle: "Si está en la zona roja, ya vamos en camino.",
      whereSub: "Pase el cursor sobre un punto para ver la ciudad. Haga clic en un condado o en un botón a la derecha para ir a su página.",
      galleryEyebrow: "Trabajos reales · cuadrillas reales",
      galleryTitle: "Desde el campo — unidades y cuadrillas reales de Faster Plumbing.",
      gallerySub: "Nuestro tráiler de servicio, vans y cuadrilla en trabajos en Palm Beach, Broward, Miami-Dade y Martin.",
      reviewsEyebrow: "Lo que dicen los clientes",
      reviewsTitle: "19 reseñas de cinco estrellas en el Sur de Florida — y siguen sumando.",
      realProject: "Proyecto real",
    },
    services: {
      heroEyebrow: "Servicios de Plomería",
      heroTitle: "Toda la plomería que su propiedad en el Sur de Florida necesita — bajo un mismo techo.",
      heroSub: "Desde un grifo que gotea hasta nueva construcción completa, nuestras cuadrillas hacen todo. Elija un servicio para ver detalles y cómo lo manejaremos.",
    },
    areas: {
      heroEyebrow: "Áreas de Servicio",
      heroTitle: "Plomeros cubriendo todo el Sur de Florida — el mismo equipo, los mismos estándares.",
      heroSub: "Con sede en Lake Worth Beach. Trabajamos en cuatro condados — elija el suyo para ver tiempos de respuesta, ciudades cubiertas y los trabajos que más hacemos en su zona.",
      pickCounty: "Elija su condado.",
    },
    about: {
      heroEyebrow: "Sobre Faster Plumbing",
      heroTitle: "12 años. Un solo oficio. El plomero del Sur de Florida.",
      heroSub: "Somos una compañía de plomería con licencia en Florida, con sede en Lake Worth Beach. Sin HVAC. Sin electricidad. Plomería — bien hecha, hoy.",
      statYears: "Años en el negocio",
      statCounties: "Condados servidos",
      stat247: "Despacho de emergencia",
      statReviews: "Reseñas 5 estrellas",
      promiseEyebrow: "Nuestra promesa",
      promiseTitle: "Respuesta rápida. A tiempo. Sin sorpresas.",
      promise1: "Contestamos el teléfono a cualquier hora, cualquier día. Nuestras unidades están equipadas para el Sur de Florida: sabemos qué falla aquí y no le hacemos perder el tiempo con viajes extra.",
      promise2: "Dueños de casa, dueños de negocio, contratistas generales — la misma cuadrilla, los mismos estándares, ya sea un cambio de grifo de $200 o una obra comercial completa. Licenciados como Florida Master Plumber #CFC1429138, totalmente asegurados.",
      fasterEyebrow: 'Qué significa "Faster"',
      fasterTitle: "Cuatro estándares. Cada trabajo. Cada cliente.",
      cards: [
        { t: "Respuesta más rápida", d: "Contestamos rápido. Llegamos rápido. Respetamos su tiempo." },
        { t: "Reparaciones más rápidas", d: "Unidades equipadas para FL. Lo arreglamos en la primera visita, casi siempre." },
        { t: "Confianza más rápida", d: "Licenciados, asegurados, dirigidos por plomeros maestros. Precios claros." },
        { t: "Servicio más rápido", d: "Técnicos que hablan español. Sitios de trabajo limpios, siempre." },
      ],
      fieldEyebrow: "Desde el campo",
      fieldTitle: "Trabajos reales. Cuadrillas reales.",
      fieldSub: "Nuestras unidades, nuestro tráiler y la cuadrilla que aparece todos los días en el Sur de Florida.",
      aboutFaqEyebrow: "Sobre nosotros · FAQ",
      teamEyebrow: "Conozca al equipo",
      teamTitle: "La familia Faster Plumbing.",
      teamSub: "Somos una empresa familiar de plomería del Sur de Florida. La misma cuadrilla en cada trabajo — sin subcontratistas rotando. Varios de nuestros técnicos llevan más de una década con nosotros.",
    },
    contact: {
      heroEyebrow: "Contacte a Faster Plumbing",
      heroTitle: "Cuéntenos qué pasa. Nosotros nos encargamos.",
      heroSubPre: "Para fugas activas, tuberías rotas o atascos de cloaca, ",
      heroSubLink: "llámenos directamente",
      heroSubPost: ". Contestamos 24/7.",
      trustBlock: ["Cotizaciones gratis por escrito", "12 años en el Sur de Florida", "Residencial · Comercial · Nueva construcción"],
      faqEyebrow: "Contacto · FAQ",
    },
    serviceDetail: {
      callbackFreeEstimate: "Cotización Gratis",
    },
    faq: {
      common: "Preguntas frecuentes",
      straight: "Respuestas claras.",
      sub: "Las preguntas que más nos hacen dueños de casa, dueños de negocio y contratistas — y lo que les decimos.",
      servicesEyebrow: "Servicios · FAQ",
      coverageEyebrow: "Cobertura · FAQ",
    },
    mapSide: {
      eyebrow: "¿Dentro de la zona roja?",
      title: "Si su dirección está en rojo, un plomero maestro ya va en camino.",
      sub: "Cuatro condados, un equipo, los mismos estándares. Elija su condado para ver tiempos de respuesta y las ciudades que más cubrimos.",
      dispatch: "Despacho",
      andInsured: "y Asegurados",
      counties: "Condados",
      licensed: "Licenciados",
    },
    process: [
      { n: "01", t: "Llame o pida cotización", d: "Cuéntenos qué pasa. Diagnosticamos por teléfono primero." },
      { n: "02", t: "Inspección y cotización en sitio", d: "Un plomero con licencia confirma el problema y le da una cotización gratis por escrito." },
      { n: "03", t: "Lo arreglamos bien", d: "Hacemos el trabajo, probamos fugas y limpiamos antes de irnos." },
    ],
  },
};
